import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Tabs,
  Tab,
  styled,
  Autocomplete,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Close as CloseIcon } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDialogContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateDataGridSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const UploadArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: '8px',
  padding: '30px 20px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.green,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const BankStatementDrawer = ({ open, onClose }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const { showSuccess, showError, showInfo } = useToast();

  // Accordion state
  const [expanded, setExpanded] = useState('bankStatements');

  // State for Bank Statements data
  const [bankStatements, setBankStatements] = useState([]);
  const [bankStatementsLoading, setBankStatementsLoading] = useState(false);
  const [bankStatementsError, setBankStatementsError] = useState(null);
  const [selectedBankStatementRow, setSelectedBankStatementRow] = useState(null);

  // State for filters
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [propertyGroupFilter, setPropertyGroupFilter] = useState('ALL');
  const [propertyIdFilter, setPropertyIdFilter] = useState('ALL');
  const [bankAccountFilter, setBankAccountFilter] = useState('ALL');
  const [reconciliationStatusFilter, setReconciliationStatusFilter] = useState('ALL');

  // State for dropdown data
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [propertyGroupsLoading, setPropertyGroupsLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);

  // State for upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedPropertyApi, setSelectedPropertyApi] = useState('');
  const [selectedBankAccountApi, setSelectedBankAccountApi] = useState('');
  const [loadingPropertyApis, setLoadingPropertyApis] = useState(false);
  const [propertyApis, setPropertyApis] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeUploadTab, setActiveUploadTab] = useState(0);

  // State for Snowflake data
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [snowflakeLoading, setSnowflakeLoading] = useState(false);
  const [snowflakeError, setSnowflakeError] = useState(null);
  const [snowflakeFromDate, setSnowflakeFromDate] = useState('');
  const [snowflakeToDate, setSnowflakeToDate] = useState('');

  // State for templates and preview
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    fileName: '',
    templateName: '',
    excelFields: [],
    templateFields: [],
    mappings: {},
    excelData: []
  });
  const [customMappings, setCustomMappings] = useState({});

  // State for Add Bank Statement Dialog
  const [addBankStatementDialogOpen, setAddBankStatementDialogOpen] = useState(false);
  const [newBankStatement, setNewBankStatement] = useState({
    bankAccountId: '',
    transactionDate: '',
    referenceNo: '',
    description: '',
    amountType: 'debit', // 'debit' or 'credit'
    amount: null,
    propertyId: '',
    currency: 'USD'
  });
  const [addBankStatementLoading, setAddBankStatementLoading] = useState(false);
  const [addBankStatementError, setAddBankStatementError] = useState(null);

  // State for Edit Bank Statement Dialog
  const [editBankStatementDialogOpen, setEditBankStatementDialogOpen] = useState(false);
  const [editingBankStatement, setEditingBankStatement] = useState(null);
  const [editBankStatementLoading, setEditBankStatementLoading] = useState(false);
  const [editBankStatementError, setEditBankStatementError] = useState(null);

  // State for Delete Bank Statement Dialog
  const [deleteBankStatementDialogOpen, setDeleteBankStatementDialogOpen] = useState(false);
  const [deletingBankStatement, setDeletingBankStatement] = useState(null);
  const [deleteBankStatementLoading, setDeleteBankStatementLoading] = useState(false);
  const [deleteBankStatementError, setDeleteBankStatementError] = useState(null);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setBankStatements([]);
      setBankStatementsError(null);
      setSelectedBankStatementRow(null);
      setDateRangeFilter({ start: '', end: '' });
      setPropertyGroupFilter('ALL');
      setPropertyIdFilter('ALL');
      setBankAccountFilter('ALL');
      setReconciliationStatusFilter('ALL');
      setUploadDialogOpen(false);
      setSelectedPropertyApi('');
      setSelectedBankAccountApi('');
      setUploadFile(null);
      setUploadProgress(0);
      setUploading(false);
      setUploadError(null);
      setIsDragging(false);
      setActiveUploadTab(0);
      setSnowflakeData([]);
      setSnowflakeError(null);
      setSnowflakeLoading(false);
      setSnowflakeFromDate('');
      setSnowflakeToDate('');
      setSelectedTemplate('');
      setTemplates([]);
      setCustomMappings({});
      setPreviewOpen(false);
      setPreviewData({
        fileName: '',
        templateName: '',
        excelFields: [],
        templateFields: [],
        mappings: {},
        excelData: []
      });
      setPropertyApis([]);
      setPropertyGroups([]);
      setProperties([]);
      setBankAccounts([]);
      setExpanded('bankStatements');
      setAddBankStatementDialogOpen(false);
      setNewBankStatement({
        bankAccountId: '',
        transactionDate: '',
        referenceNo: '',
        description: '',
        amountType: 'debit',
        amount: null,
        propertyId: '',
        currency: 'USD'
      });
      setEditBankStatementDialogOpen(false);
      setEditingBankStatement(null);
      setDeleteBankStatementDialogOpen(false);
      setDeletingBankStatement(null);
      setAddBankStatementError(null);
      setEditBankStatementError(null);
      setDeleteBankStatementError(null);
    }
  }, [open]);

  // Fetch property groups for dropdown
  const fetchPropertyGroups = async () => {
    setPropertyGroupsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/property-groups/by-tenant?tenant_id=${tenantId}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch property groups: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        setPropertyGroups(data);
      } else {
        setPropertyGroups([]);
      }
    } catch (err) {
      console.error('Error fetching property groups:', err);
      setPropertyGroups([]);
    } finally {
      setPropertyGroupsLoading(false);
    }
  };

  // Fetch bank accounts for dropdown
  const fetchBankAccounts = async () => {
    setBankAccountsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      // const url = `${baseUrl}conductor/cash-recon/banks/by-tenant?tenant_id=${tenantId}`;
      const url = `${baseUrl}conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        // If API doesn't exist yet, return empty array
        if (resp.status === 404) {
          setBankAccounts([]);
          return;
        }
        throw new Error(`Failed to fetch banks: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        // Map bank data to match expected format for dropdown
        const processedData = data.map((bank) => ({
          bankAccountId: bank.bankId,
          bankId: bank.bankId,
          accountName: bank.bankName,
          accountNumber: bank.bankCode,
          bankName: bank.bankName,
          bankShortName: bank.bankShortName,
          bankCode: bank.bankCode,
          swiftBic: bank.swiftBic,
          country: bank.country,
          website: bank.website,
          status: bank.status,
          tenantId: bank.tenantId
        }));
        setBankAccounts(processedData);
      } else {
        setBankAccounts([]);
      }
    } catch (err) {
      console.error('Error fetching banks:', err);
      setBankAccounts([]);
    } finally {
      setBankAccountsLoading(false);
    }
  };

  // Fetch Bank Statements data
  const fetchBankStatements = async () => {
    setBankStatementsLoading(true);
    setBankStatementsError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Build URL with filters - tenant_id is mandatory
      const params = new URLSearchParams();
      params.append('tenant_id', tenantId);

      if (dateRangeFilter.start) {
        params.append('start_date', dateRangeFilter.start);
      }

      if (dateRangeFilter.end) {
        params.append('end_date', dateRangeFilter.end);
      }

      if (bankAccountFilter !== 'ALL') {
        params.append('bank_account_id', bankAccountFilter);
      }

      if (propertyIdFilter !== 'ALL') {
        params.append('property_id', propertyIdFilter);
      }

      if (propertyGroupFilter !== 'ALL') {
        params.append('property_group', propertyGroupFilter);
      }

      if (reconciliationStatusFilter !== 'ALL') {
        params.append('recon_status', reconciliationStatusFilter);
      }

      const url = `${baseUrl}conductor/cash-recon/bank-statements/by-tenant-filter?${params.toString()}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        // If API doesn't exist yet, return mock data
        if (resp.status === 404) {
          const mockData = [
            {
              id: '4c607529-e922-4079-9b24-5b4638ac1d17',
              bankAccountId: '550e8400-e29b-41d4-a716-446655440000',
              transactionDate: '2024-01-15',
              referenceNo: 'REF001',
              description: 'Vendor Payment',
              debitAmount: 2000.50,
              creditAmount: null,
              createdAt: '2026-02-10 11:06:34',
              updatedAt: '2026-02-10 11:09:05',
              tenantId: tenantId,
              propertyId: '9c080315-a4b5-4810-bbd3-f2c53c080a4c',
              propertyGroup: 'Group A',
              intrgrationId: null,
              currency: 'USD',
              amount: 2000.50,
              txnDirection: 'DEBIT',
              sourceSystem: 'Manual', // Added sourceSystem field
              reconStatus: 'UNMATCHED',
              reconId: null,
              matchedGlTxnId: null,
              matchConfidence: null,
              matchedBy: null,
              matchedAt: null,
              normalizedRef: null,
              normalizedDesc: null,
              hashKey: null,
              notes: null,
              isReadOnly: false
            },
            {
              id: '5d71863a-f033-5180-0c35-6c5749bd2e28',
              bankAccountId: '550e8400-e29b-41d4-a716-446655440000',
              transactionDate: '2024-01-16',
              referenceNo: 'REF002',
              description: 'Customer Deposit',
              debitAmount: null,
              creditAmount: 5000.00,
              createdAt: '2026-02-10 11:06:34',
              updatedAt: '2026-02-10 11:09:05',
              tenantId: tenantId,
              propertyId: '9c080315-a4b5-4810-bbd3-f2c53c080a4c',
              propertyGroup: 'Group A',
              intrgrationId: null,
              currency: 'USD',
              amount: 5000.00,
              txnDirection: 'CREDIT',
              sourceSystem: 'API', // Added sourceSystem field
              reconStatus: 'AUTO_MATCHED',
              reconId: 'recon-001',
              matchedGlTxnId: 'gl-txn-001',
              matchConfidence: 0.95,
              matchedBy: 'SYSTEM',
              matchedAt: '2026-02-10 11:10:00',
              normalizedRef: 'REF002',
              normalizedDesc: 'CUSTOMER DEPOSIT',
              hashKey: 'hash-002',
              notes: 'Auto matched with GL transaction',
              isReadOnly: true
            }
          ];
          setBankStatements(mockData);
          return;
        }
        throw new Error(`Failed to fetch bank statements: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && Array.isArray(data)) {
        const processedData = data.map((item, index) => ({
          id: item.id || index + 1,
          bankAccountId: item.bankAccountId || '',
          transactionDate: item.transactionDate || '',
          referenceNo: item.referenceNo || '',
          description: item.description || '',
          debitAmount: item.debitAmount || 0,
          creditAmount: item.creditAmount || 0,
          currency: item.currency || 'USD',
          amount: item.amount || 0,
          txnDirection: item.txnDirection || '',
          propertyId: item.propertyId || '',
          propertyGroup: item.propertyGroup || '',
          intrgrationId: item.intrgrationId || null,
          sourceSystem: item.sourceSystem || (item.intrgrationId ? 'API' : 'Manual'), // Fixed: Use sourceSystem from API
          reconStatus: item.reconStatus || 'UNMATCHED',
          reconId: item.reconId || null,
          matchedGlTxnId: item.matchedGlTxnId || null,
          matchConfidence: item.matchConfidence || null,
          matchedBy: item.matchedBy || null,
          matchedAt: item.matchedAt || null,
          normalizedRef: item.normalizedRef || null,
          normalizedDesc: item.normalizedDesc || null,
          hashKey: item.hashKey || null,
          notes: item.notes || null,
          isReadOnly: item.reconStatus === 'APPROVED' || item.reconStatus === 'AUTO_MATCHED' || item.approvalStatus === 'APPROVED',
          createdAt: item.createdAt || '',
          updatedAt: item.updatedAt || '',
          rawData: item,
        }));
        setBankStatements(processedData);
      } else {
        setBankStatements([]);
      }
    } catch (err) {
      console.error('Error fetching bank statements:', err);
      setBankStatementsError(err.message || 'Failed to load bank statements');
      setBankStatements([]);
    } finally {
      setBankStatementsLoading(false);
    }
  };

  // Initial data bootstrap when drawer opens
  useEffect(() => {
    if (!open) return;
    fetchPropertyGroups();
    fetchBankAccounts();
    fetchPropertyApis();
  }, [open]);

  // Re-fetch bank statements when filters change (or drawer opens)
  useEffect(() => {
    if (!open) return;
    fetchBankStatements();
  }, [open, dateRangeFilter, propertyGroupFilter, propertyIdFilter, bankAccountFilter, reconciliationStatusFilter]);

  // Fetch Properties (using correct API endpoint)
  const fetchPropertyApis = async () => {
    setLoadingPropertyApis(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && Array.isArray(data)) {
        const processedData = data.map((item, index) => ({
          id: item.propertyId || index + 1,
          propertyId: item.propertyId || '',
          propertyName: item.propertyName || '',
          propertyCode: item.propertyCode || '',
          propertyGroupId: item.propertyGroupId || '',
          companyId: item.companyId || '',
          addressLine1: item.addressLine1 || '',
          city: item.city || '',
          state: item.state || '',
          country: item.country || '',
          postalCode: item.postalCode || '',
          baseCurrency: item.baseCurrency || 'USD',
          defaultBankAccountId: item.defaultBankAccountId || null,
          cashReconEnabled: item.cashReconEnabled !== false,
          status: item.status || 'ACTIVE',
          deleted: item.deleted || false,
          createdAt: item.createdAt || '',
          updatedAt: item.updatedAt || '',
        }));
        setPropertyApis(processedData);
      } else {
        setPropertyApis([]);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setPropertyApis([]);
    } finally {
      setLoadingPropertyApis(false);
    }
  };

  // Keep dropdown properties aligned with selected property group without extra by-tenant calls.
  useEffect(() => {
    if (!open) return;

    if (propertyGroupFilter === 'ALL') {
      setProperties(propertyApis);
      return;
    }

    const filteredProperties = propertyApis.filter(
      p => p.propertyGroupId === propertyGroupFilter
    );
    setProperties(filteredProperties);
  }, [open, propertyGroupFilter, propertyApis]);


  const a11yProps = (index) => ({
    id: `bs-tab-${index}`,
    'aria-controls': `bs-tabpanel-${index}`,
  });

  const CustomTabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bs-tabpanel-${index}`}
      aria-labelledby={`bs-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );

  const fetchSnowflakeData = async () => {
    if (!selectedPropertyApi) {
      setSnowflakeData([]);
      setSnowflakeError('Please select a property to load Snowflake data');
      return;
    }

    setSnowflakeLoading(true);
    setSnowflakeError(null);
    setUploadError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/bank-normalized-data-view-import?tenant_id=${tenantId}`;

      if (snowflakeFromDate) apiUrl += `&start_date=${snowflakeFromDate}`;
      if (snowflakeToDate) apiUrl += `&end_date=${snowflakeToDate}`;

      // Find the selected property to get its name
      const selectedProperty = propertyApis.find(property => property.propertyId === selectedPropertyApi);
      if (selectedProperty && selectedProperty.propertyName) {
        apiUrl += `&property_name=${encodeURIComponent(selectedProperty.propertyName)}`;
      }

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle API errors
      if (!resp.ok) {
        console.error(`API returned ${resp.status} error`);
        setSnowflakeData([]); // Show empty grid on error
        setSnowflakeError(`Failed to load data: ${resp.status} ${resp.statusText}`);
        return;
      }

      const data = await resp.json();

      if (data.status === 'success' && Array.isArray(data.data)) {
        // Check if API has data
        if (data.data.length > 0) {
          const processedData = data.data.map((item, index) => ({
            id: item.CASH_RECON_ID || index + 1,
            description: item.BANK_DESCRIPTION || item.NEW_CONTENT?.BANK_DESCRIPTION || '',
            date: item.BANK_TRANSACTION_DATE || item.NEW_CONTENT?.BANK_TRANSACTION_DATE || '',
            docNo: item.BANK_DOCUMENT_NO || item.NEW_CONTENT?.BANK_DOCUMENT_NO || item.BANK_REFERENCE || item.NEW_CONTENT?.BANK_REFERENCE || '',
            debit: Math.abs(item.BANK_DEBITS || item.NEW_CONTENT?.BANK_DEBITS || 0),
            credit: Math.abs(item.BANK_CREDITS || item.NEW_CONTENT?.BANK_CREDITS || 0),
            originalData: item,
          }));
          setSnowflakeData(processedData);
        } else {
          // API returned empty data, show empty grid
          setSnowflakeData([]);
        }
      } else {
        throw new Error('Invalid response format from Snowflake API');
      }
    } catch (err) {
      setSnowflakeError(err.message);
      setSnowflakeData([]);
      showError(err.message || 'Failed to fetch Snowflake data');
    } finally {
      setSnowflakeLoading(false);
    }
  };

  // Fetch templates for Bank Statements
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // For Bank Statements, we'll use a specific endpoint or filter
      const url = `${baseUrl}conductor/ref-templates/by-document-type?documentTypeId=BANK_STATEMENT&tenant_id=${tenantId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        // If the endpoint doesn't exist, create default templates
        if (resp.status === 404) {
          const defaultTemplates = [
            {
              id: 'bank_statement_default',
              name: 'Bank Statement Default',
              description: 'Default template for Bank Statement upload',
              mappings: {
                'TRANSACTION_DATE': { targetColumn: 'transactionDate', dataType: 'DATE', isRequired: true },
                'REFERENCE_NUMBER': { targetColumn: 'referenceNo', dataType: 'TEXT', isRequired: false },
                'DESCRIPTION': { targetColumn: 'description', dataType: 'TEXT', isRequired: true },
                'DEBIT': { targetColumn: 'debitAmount', dataType: 'NUMBER', isRequired: false },
                'CREDIT': { targetColumn: 'creditAmount', dataType: 'NUMBER', isRequired: false },
                'GL_ACCOUNT': { targetColumn: 'glAccount', dataType: 'TEXT', isRequired: false }
              }
            }
          ];
          setTemplates(defaultTemplates);
        } else {
          throw new Error(`Failed to fetch templates: ${resp.status} ${resp.statusText}`);
        }
      } else {
        const data = await resp.json();
        const transformedTemplates = Array.isArray(data) ? data.map(template => ({
          id: template.templateId,
          name: template.templateName,
          description: template.description,
          mappings: template.mapping || {}
        })) : [];
        setTemplates(transformedTemplates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      // Create default templates on error
      const defaultTemplates = [
        {
          id: 'bank_statement_default',
          name: 'Bank Statement Default',
          description: 'Default template for Bank Statement upload',
          mappings: {
            'TRANSACTION_DATE': { targetColumn: 'transactionDate', dataType: 'DATE', isRequired: true },
            'REFERENCE_NUMBER': { targetColumn: 'referenceNo', dataType: 'TEXT', isRequired: false },
            'DESCRIPTION': { targetColumn: 'description', dataType: 'TEXT', isRequired: true },
            'DEBIT': { targetColumn: 'debitAmount', dataType: 'NUMBER', isRequired: false },
            'CREDIT': { targetColumn: 'creditAmount', dataType: 'NUMBER', isRequired: false },
            'GL_ACCOUNT': { targetColumn: 'glAccount', dataType: 'TEXT', isRequired: false }
          }
        }
      ];
      setTemplates(defaultTemplates);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Fetch templates when dialog opens
  useEffect(() => {
    if (uploadDialogOpen) {
      fetchTemplates();
    }
  }, [uploadDialogOpen]);

  // Function to dynamically load XLSX library
  const loadXLSX = async () => {
    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
      return XLSX;
    } catch (error) {
      console.warn('Failed to load XLSX from CDN, trying local import:', error);
      const XLSX = await import('xlsx');
      return XLSX;
    }
  };

  // Convert value based on data type
  const convertValue = (value, dataType) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const strValue = String(value).trim();
    const type = (dataType || 'TEXT').toUpperCase();

    switch (type) {
      case 'NUMBER':
      case 'INTEGER':
      case 'FLOAT':
      case 'DECIMAL':
        const numValue = strValue.replace(/[^0-9.-]/g, '');
        return numValue === '' ? null : Number(numValue);
      case 'BOOLEAN':
        const lowerValue = strValue.toLowerCase();
        if (['true', 'yes', 'y', '1', 't'].includes(lowerValue)) return true;
        if (['false', 'no', 'n', '0', 'f'].includes(lowerValue)) return false;
        return Boolean(strValue);
      case 'DATE':
        // Handle Excel serial date numbers (e.g. 45678)
        const numericValue = parseFloat(strValue);
        if (!isNaN(numericValue) && numericValue > 0 && numericValue < 100000) {
          // Excel epoch: 1900-01-01, adjust for Excel's leap year bug
          const excelEpoch = new Date(1900, 0, 1);
          const daysOffset = numericValue - 1;
          const jsDate = new Date(excelEpoch.getTime() + (daysOffset * 24 * 60 * 60 * 1000));
          return jsDate.toISOString().split('T')[0];
        }
        // Handle MM/dd/yyyy string format
        if (strValue.includes('/')) {
          const parts = strValue.split('/');
          if (parts.length === 3) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
          }
        }
        // Try native Date parse
        const date = new Date(strValue);
        return isNaN(date.getTime()) ? strValue : date.toISOString().split('T')[0];
      case 'TEXT':
      default:
        return strValue.replace(/\s+/g, ' ').trim();
    }
  };

  // Extract fields and data from Excel file
  const extractFieldsFromSheet = async (file) => {
    try {
      const XLSX = await loadXLSX();
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      if (!jsonData || jsonData.length === 0) {
        return { fields: [], data: [] };
      }

      const headers = jsonData[0].map(h => h ? h.toString().trim() : '').filter(h => h !== '' && h !== '*' && h.trim() !== '');
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));

      const dataRows = rows.map(row => {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = row[index] || '';
        });
        return obj;
      });

      return { fields: headers, data: dataRows };
    } catch (error) {
      console.error('Error extracting fields from sheet:', error);
      return { fields: [], data: [] };
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        setUploadFile(file);
        setUploadError(null);
      } else {
        setUploadError('Please select a CSV or Excel file');
      }
    }
  };

  // Handle preview button click
  const handlePreviewClick = async () => {
    if (!selectedTemplate || !uploadFile) return;

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      if (!template) {
        throw new Error('Selected template not found');
      }

      const { fields: excelFields, data: excelData } = await extractFieldsFromSheet(uploadFile);

      // Build field mappings
      const fieldMappings = {};
      const templateFields = [];

      if (template.mappings) {
        Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
          if (mapping && mapping.isActive !== false) {
            const matchedField = excelFields.find(field =>
              field.toLowerCase() === sourceField.toLowerCase()
            );

            if (matchedField) {
              fieldMappings[sourceField] = {
                targetColumn: mapping.targetColumn || sourceField,
                dataType: mapping.dataType || 'TEXT',
                isRequired: mapping.isRequired || false,
                excelField: matchedField
              };
              templateFields.push(sourceField);
            }
          }
        });
      }

      // Apply custom mappings
      Object.entries(customMappings).forEach(([excelField, templateField]) => {
        if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
          const mapping = template.mappings?.[templateField];
          fieldMappings[templateField] = {
            targetColumn: mapping?.targetColumn || templateField,
            dataType: mapping?.dataType || 'TEXT',
            isRequired: mapping?.isRequired || false,
            excelField: excelField
          };
          if (!templateFields.includes(templateField)) {
            templateFields.push(templateField);
          }
        }
      });

      // Process excel data
      const processedData = excelData.map(row => {
        const mappedRow = {};
        templateFields.forEach(field => {
          const mapping = fieldMappings[field];
          if (mapping?.excelField && row[mapping.excelField] !== undefined) {
            mappedRow[field] = convertValue(row[mapping.excelField], mapping.dataType);
          }
        });
        return mappedRow;
      });

      setPreviewData({
        fileName: uploadFile.name,
        templateName: template.name || 'Unnamed Template',
        excelFields: excelFields,
        templateFields: templateFields,
        mappings: fieldMappings,
        excelData: processedData
      });

      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setUploadError(`Failed to generate preview: ${error.message}`);
    }
  };

  // Handle custom field mapping
  const handleCustomMapping = (excelField, templateField) => {
    setCustomMappings(prev => {
      const newMappings = { ...prev };

      // Remove any previous mapping for this excel field
      Object.keys(newMappings).forEach(key => {
        if (newMappings[key] === templateField) {
          delete newMappings[key];
        }
      });

      // Add new mapping if templateField is provided
      if (templateField && templateField !== '') {
        newMappings[excelField] = templateField;
      } else {
        delete newMappings[excelField];
      }

      return newMappings;
    });
  };

  // Handle preview apply
  const handlePreviewApply = () => {
    setPreviewOpen(false);
    showSuccess('Field mappings applied successfully');
  };

  // Handle template selection change
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    setCustomMappings({}); // Reset custom mappings when template changes
  };

  // Get available template fields for mapping
  const getAvailableTemplateFields = (currentExcelField, currentMappedTemplateField) => {
    if (!selectedTemplate) return [];

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return [];

    const allTemplateFields = Object.keys(template.mappings || {})
      .filter(field => {
        const mapping = template.mappings[field];
        return mapping && mapping.isActive !== false;
      });

    // Get currently used template fields from mappings
    const usedFields = new Set();
    Object.entries(customMappings).forEach(([excelField, templateField]) => {
      if (excelField !== currentExcelField) {
        usedFields.add(templateField);
      }
    });

    // Return all fields that are either unused or currently selected
    return allTemplateFields
      .filter(field => !usedFields.has(field) || field === currentMappedTemplateField)
      .sort((a, b) => {
        // Sort required fields first, then alphabetically
        const aRequired = template.mappings[a]?.isRequired ? 0 : 1;
        const bRequired = template.mappings[b]?.isRequired ? 0 : 1;

        if (aRequired !== bRequired) {
          return aRequired - bRequired;
        }

        return a.localeCompare(b);
      });
  };

  // Handle Add Bank Statement
  const handleAddBankStatement = async () => {
    if (!newBankStatement.bankAccountId.trim()) {
      setAddBankStatementError('Bank Account is required');
      return;
    }
    if (!newBankStatement.transactionDate.trim()) {
      setAddBankStatementError('Transaction Date is required');
      return;
    }
    if (!newBankStatement.description.trim()) {
      setAddBankStatementError('Description is required');
      return;
    }
    if (!newBankStatement.amount) {
      setAddBankStatementError('Amount is required');
      return;
    }

    setAddBankStatementLoading(true);
    setAddBankStatementError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/bank-statements`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Prepare the request body according to the API specification
      const requestBody = {
        tenantId: tenantId,
        bankAccountId: newBankStatement.bankAccountId.trim(),
        transactionDate: newBankStatement.transactionDate.trim(),
        referenceNo: newBankStatement.referenceNo.trim() || null,
        description: newBankStatement.description.trim(),
        debitAmount: newBankStatement.amountType === 'debit' ? newBankStatement.amount : null,
        creditAmount: newBankStatement.amountType === 'credit' ? newBankStatement.amount : null,
        propertyId: newBankStatement.propertyId.trim() || null,
        currency: newBankStatement.currency || "USD",
        sourceSystem: 'MANUAL'
      };

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        throw new Error(`Failed to add bank statement: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      // Add the new statement to the local state with the response data
      const newStatement = {
        id: data.id || `temp-${bankStatements.length + 1}`,
        bankAccountId: data.bankAccountId || newBankStatement.bankAccountId,
        transactionDate: data.transactionDate || newBankStatement.transactionDate,
        referenceNo: data.referenceNo || newBankStatement.referenceNo,
        description: data.description || newBankStatement.description,
        debitAmount: data.debitAmount || null,
        creditAmount: data.creditAmount || null,
        currency: data.currency || newBankStatement.currency,
        amount: data.amount || newBankStatement.amount,
        txnDirection: data.txnDirection || newBankStatement.amountType.toUpperCase(),
        propertyId: data.propertyId || newBankStatement.propertyId,
        propertyGroup: data.propertyGroup || '',
        intrgrationId: data.intrgrationId || null,
        sourceIntegration: data.intrgrationId ? 'API' : 'Manual',
        reconStatus: data.reconStatus || 'UNMATCHED',
        reconId: data.reconId || null,
        matchedGlTxnId: data.matchedGlTxnId || null,
        matchConfidence: data.matchConfidence || null,
        matchedBy: data.matchedBy || null,
        matchedAt: data.matchedAt || null,
        normalizedRef: data.normalizedRef || null,
        normalizedDesc: data.normalizedDesc || null,
        hashKey: data.hashKey || null,
        notes: data.notes || null,
        isReadOnly: data.reconStatus === 'APPROVED' || data.reconStatus === 'AUTO_MATCHED' || data.approvalStatus === 'APPROVED',
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
        ...data
      };

      setBankStatements([...bankStatements, newStatement]);
      showSuccess('Bank Statement added successfully');
      setAddBankStatementDialogOpen(false);
      setNewBankStatement({
        bankAccountId: '',
        transactionDate: '',
        referenceNo: '',
        description: '',
        amountType: 'debit',
        amount: null,
        propertyId: '',
        currency: 'USD'
      });
    } catch (err) {
      console.error('Error adding bank statement:', err);
      setAddBankStatementError(err.message || 'Failed to add bank statement');
    } finally {
      setAddBankStatementLoading(false);
    }
  };

  // Handle Edit Bank Statement
  const handleEditBankStatement = async () => {
    if (!editingBankStatement || !editingBankStatement.description.trim()) {
      setEditBankStatementError('Description is required');
      return;
    }
    if (!editingBankStatement.amount) {
      setEditBankStatementError('Amount is required');
      return;
    }

    setEditBankStatementLoading(true);
    setEditBankStatementError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Use the id for the URL parameter
      const statementId = editingBankStatement.id;
      const url = `${baseUrl}conductor/cash-recon/bank-statements/${statementId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Prepare the request body according to the API specification
      const requestBody = {
        tenantId: tenantId,
        bankAccountId: editingBankStatement.bankAccountId || '',
        transactionDate: editingBankStatement.transactionDate || '',
        referenceNo: editingBankStatement.referenceNo || '',
        description: editingBankStatement.description || '',
        debitAmount: editingBankStatement.amountType === 'debit' ? editingBankStatement.amount : null,
        creditAmount: editingBankStatement.amountType === 'credit' ? editingBankStatement.amount : null,
        propertyId: editingBankStatement.propertyId || null,
        currency: editingBankStatement.currency || "USD",
        sourceSystem: 'MANUAL'
      };

      const resp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        throw new Error(`Failed to update bank statement: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      // Update the statement in the local state with the response data
      const updatedStatements = bankStatements.map(statement =>
        statement.id === statementId
          ? {
            ...statement,
            id: data.id || statement.id,
            bankAccountId: data.bankAccountId || editingBankStatement.bankAccountId,
            transactionDate: data.transactionDate || editingBankStatement.transactionDate,
            referenceNo: data.referenceNo || editingBankStatement.referenceNo,
            description: data.description || editingBankStatement.description,
            debitAmount: data.debitAmount || null,
            creditAmount: data.creditAmount || null,
            currency: data.currency || editingBankStatement.currency,
            amount: data.amount || editingBankStatement.amount,
            txnDirection: data.txnDirection || editingBankStatement.amountType.toUpperCase(),
            propertyId: data.propertyId || editingBankStatement.propertyId,
            propertyGroup: data.propertyGroup || statement.propertyGroup,
            intrgrationId: data.intrgrationId || statement.intrgrationId,
            sourceIntegration: data.intrgrationId ? 'API' : statement.sourceIntegration,
            reconStatus: data.reconStatus || statement.reconStatus,
            reconId: data.reconId || statement.reconId,
            matchedGlTxnId: data.matchedGlTxnId || statement.matchedGlTxnId,
            matchConfidence: data.matchConfidence || statement.matchConfidence,
            matchedBy: data.matchedBy || statement.matchedBy,
            matchedAt: data.matchedAt || statement.matchedAt,
            normalizedRef: data.normalizedRef || statement.normalizedRef,
            normalizedDesc: data.normalizedDesc || statement.normalizedDesc,
            hashKey: data.hashKey || statement.hashKey,
            notes: data.notes || statement.notes,
            isReadOnly: data.reconStatus === 'APPROVED' || data.reconStatus === 'AUTO_MATCHED' || data.approvalStatus === 'APPROVED',
            updatedAt: data.updatedAt || new Date().toISOString(),
            ...data
          }
          : statement
      );

      setBankStatements(updatedStatements);
      showSuccess('Bank Statement updated successfully');
      setEditBankStatementDialogOpen(false);
      setEditingBankStatement(null);
    } catch (err) {
      console.error('Error updating bank statement:', err);
      setEditBankStatementError(err.message || 'Failed to update bank statement');
    } finally {
      setEditBankStatementLoading(false);
    }
  };

  // Handle Delete Bank Statement
  const handleDeleteBankStatement = async () => {
    if (!deletingBankStatement) return;

    setDeleteBankStatementLoading(true);
    setDeleteBankStatementError(null);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Use the id for the URL parameter
      const statementId = deletingBankStatement.id;
      const url = `${baseUrl}conductor/cash-recon/bank-statements/${statementId}`;

      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to delete bank statement: ${resp.status} ${resp.statusText}`);
      }

      // Handle 204 No Content response (successful deletion)
      if (resp.status === 204) {
        showSuccess('Bank Statement deleted successfully');
        await fetchBankStatements();
        setDeleteBankStatementDialogOpen(false);
        setDeletingBankStatement(null);
        return;
      }

      // Remove the deleted statement from the local state
      const updatedStatements = bankStatements.filter(statement => statement.id !== statementId);

      setBankStatements(updatedStatements);
      showSuccess('Bank Statement deleted successfully');
      setDeleteBankStatementDialogOpen(false);
      setDeletingBankStatement(null);
    } catch (err) {
      console.error('Error deleting bank statement:', err);
      setDeleteBankStatementError(err.message || 'Failed to delete bank statement');
    } finally {
      setDeleteBankStatementLoading(false);
    }
  };

  // Open Add Bank Statement Dialog
  const handleOpenAddBankStatementDialog = (e) => {
    if (!canAdd) return;
    e.stopPropagation();
    setAddBankStatementDialogOpen(true);
    setAddBankStatementError(null);
  };

  // Open Edit Bank Statement Dialog
  const handleOpenEditBankStatementDialog = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    if (!selectedBankStatementRow) {
      showError('Please select a bank statement to edit');
      return;
    }
    if (selectedBankStatementRow.isReadOnly) {
      if (selectedBankStatementRow.rawData?.approvalStatus === 'APPROVED') {
        showError('Cannot edit a statement that is already approved');
      } else {
        showError('Cannot edit a statement that is already reconciled');
      }
      return;
    }
    // const hasDebit = parseFloat(selectedBankStatementRow.debitAmount) > 0;
    const debitAmt = parseFloat(selectedBankStatementRow.debitAmount ?? selectedBankStatementRow.rawData?.debitAmount) || 0;
    const creditAmt = parseFloat(selectedBankStatementRow.creditAmount ?? selectedBankStatementRow.rawData?.creditAmount) || 0;
    const hasDebit = debitAmt > 0;
    setEditingBankStatement({
      ...selectedBankStatementRow,
      bankAccountId: selectedBankStatementRow.bankAccountId || selectedBankStatementRow.rawData?.bankAccountId || '',
      propertyId: selectedBankStatementRow.propertyId || selectedBankStatementRow.rawData?.propertyId || '',
      transactionDate: selectedBankStatementRow.transactionDate || selectedBankStatementRow.rawData?.transactionDate || '',
      referenceNo: selectedBankStatementRow.referenceNo || selectedBankStatementRow.rawData?.referenceNo || '',
      description: selectedBankStatementRow.description || selectedBankStatementRow.rawData?.description || '',
      currency: selectedBankStatementRow.currency || selectedBankStatementRow.rawData?.currency || 'USD',
      amountType: hasDebit ? 'debit' : 'credit',
      amount: hasDebit ? debitAmt : creditAmt,
    });
    setEditBankStatementDialogOpen(true);
    setEditBankStatementError(null);
  };

  // Open Delete Bank Statement Dialog
  const handleOpenDeleteBankStatementDialog = (e) => {
    if (!canDelete) return;
    e.stopPropagation();
    if (!selectedBankStatementRow) {
      showError('Please select a bank statement to delete');
      return;
    }
    if (selectedBankStatementRow.rawData?.approvalStatus === 'APPROVED') {
      showError('Cannot delete a statement that is already approved');
      return;
    }

    // Check if the record is mapped
    const reconStatus = selectedBankStatementRow.reconStatus || selectedBankStatementRow.rawData?.reconStatus;
    if (reconStatus === 'AUTO_MATCHED' || reconStatus === 'MANUAL_MATCHED') {
      setDeletingBankStatement(selectedBankStatementRow);
      setDeleteBankStatementDialogOpen(true);
      setDeleteBankStatementError('This record is mapped with a GL Transaction, cannot delete');
      return;
    }

    setDeletingBankStatement(selectedBankStatementRow);
    setDeleteBankStatementDialogOpen(true);
    setDeleteBankStatementError(null);
  };

  // CSV Upload Functions
  const handleOpenUploadDialog = (e) => {
    e.stopPropagation();
    setUploadDialogOpen(true);
    setUploadError(null);
    setUploadFile(null);
    setSelectedPropertyApi('');  // Reset property selection
    setSelectedBankAccountApi('');  // Reset bank account selection
    setSelectedTemplate('');  // Reset template selection
    setCustomMappings({});  // Reset custom mappings
    setActiveUploadTab(0);  // Reset to first tab
  };

  // Handle Save Snowflake Data
  const handleSaveSnowflakeData = async () => {
    if (!selectedPropertyApi || !selectedBankAccountApi || snowflakeData.length === 0) {
      setUploadError('Please select a Property, Bank Account and load Snowflake data.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/bank-statements/bulk`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Map Snowflake data to bulk API format
      const statementsData = snowflakeData.map((item) => {
        // Use the transformed field names from fetchSnowflakeData
        const debitAmount = item.debit > 0 ? item.debit : null;
        const creditAmount = item.credit > 0 ? item.credit : null;
        const txnDirection = debitAmount > 0 ? 'DEBIT' : 'CREDIT';
        const amount = debitAmount > 0 ? debitAmount : creditAmount;

        // Validation: At least one of debitAmount or creditAmount must be provided
        if (!debitAmount && !creditAmount) {
          console.warn('Skipping record - no debit or credit amount found:', item);
          return null;
        }

        // Validation: Transaction date is required
        if (!item.date) {
          console.warn('Skipping record - no transaction date found:', item);
          return null;
        }

        return {
          tenantId: tenantId,
          bankAccountId: selectedBankAccountApi,
          propertyId: selectedPropertyApi,
          transactionDate: item.date, // Use transformed 'date' field
          referenceNo: item.docNo || '',
          description: item.description || '',
          debitAmount: debitAmount,
          creditAmount: creditAmount,
          currency: 'USD',
          intrgrationId: null,
          sourceSystem: 'SNOWFLAKE',
          externalTxnId: item.id?.toString() || '', // Use transformed 'id' field
          txnDirection: txnDirection,
          amount: amount,
          reconStatus: 'UNMATCHED'
        };
      }).filter(item => item !== null); // Remove null entries

      // Additional validation: Ensure we have valid records to save
      if (statementsData.length === 0) {
        throw new Error('No valid records found. Each record must have a transaction date and either a debit or credit amount.');
      }

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(statementsData),
      });

      if (!resp.ok) {
        let errorMessage = `Failed to save Snowflake data: ${resp.status} ${resp.statusText}`;

        try {
          const errorData = await resp.json();
          if (errorData.error) {
            errorMessage = errorData.error; // Show the actual error message from API
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          // If response is not JSON, try to get it as text
          try {
            const errorText = await resp.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Keep the default error message
          }
        }

        throw new Error(errorMessage);
      }

      const result = await resp.json();

      // Refresh the bank statements data
      await fetchBankStatements();

      const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
      const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
      showSuccess(`Successfully saved ${result.length || statementsData.length} Snowflake records for ${propertyName}`);
      setUploadDialogOpen(false);
      setSnowflakeData([]);
      setSelectedPropertyApi('');
      setSelectedBankAccountApi('');
      setSnowflakeFromDate('');
      setSnowflakeToDate('');

    } catch (apiError) {
      console.error('API Error:', apiError);
      setUploadError(apiError.message || 'Failed to save Snowflake data');
    } finally {
      setUploading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!selectedPropertyApi || !selectedBankAccountApi || !uploadFile) {
      setUploadError('Please select a Property, Bank Account and upload a file.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/bank-statements/bulk`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const isExcel = uploadFile.name.endsWith('.xls') || uploadFile.name.endsWith('.xlsx');

      // ── EXCEL PATH (same as GLTransactionDrawer) ──────────────────────────
      if (isExcel || selectedTemplate) {
        const { fields: excelFields, data: excelData } = await extractFieldsFromSheet(uploadFile);

        if (excelFields.length === 0) {
          setUploadError('Could not read headers from file.');
          setUploading(false);
          return;
        }

        let statementsData = [];

        if (selectedTemplate) {
          // Template-based mapping
          const template = templates.find(t => t.id === selectedTemplate);
          if (template) {
            const fieldMappings = {};
            const templateFields = [];

            Object.entries(template.mappings || {}).forEach(([sourceField, mapping]) => {
              if (mapping && mapping.isActive !== false) {
                const matchedField = excelFields.find(f =>
                  f.toLowerCase() === sourceField.toLowerCase()
                );
                if (matchedField) {
                  fieldMappings[sourceField] = {
                    targetColumn: mapping.targetColumn || sourceField,
                    dataType: mapping.dataType || 'TEXT',
                    isRequired: mapping.isRequired || false,
                    excelField: matchedField
                  };
                  templateFields.push(sourceField);
                }
              }
            });

            // Apply custom mappings
            Object.entries(customMappings).forEach(([excelField, templateField]) => {
              if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
                const mapping = template.mappings?.[templateField];
                fieldMappings[templateField] = {
                  targetColumn: mapping?.targetColumn || templateField,
                  dataType: mapping?.dataType || 'TEXT',
                  isRequired: mapping?.isRequired || false,
                  excelField: excelField
                };
                if (!templateFields.includes(templateField)) templateFields.push(templateField);
              }
            });

            statementsData = excelData.map(row => {
              const mappedRow = {
                tenantId,
                bankAccountId: selectedBankAccountApi,
                propertyId: selectedPropertyApi,
                transactionDate: '',
                referenceNo: '',
                description: '',
                debitAmount: null,
                creditAmount: null,
                sourceSystem: 'CSV'
              };

              templateFields.forEach(field => {
                const mapping = fieldMappings[field];
                if (mapping?.excelField && row[mapping.excelField] !== undefined) {
                  const value = convertValue(row[mapping.excelField], mapping.dataType);
                  switch (mapping.targetColumn) {
                    case 'transactionDate': mappedRow.transactionDate = value || ''; break;
                    case 'referenceNo': mappedRow.referenceNo = value || ''; break;
                    case 'description': mappedRow.description = value || ''; break;
                    case 'debitAmount': mappedRow.debitAmount = value || null; break;
                    case 'creditAmount': mappedRow.creditAmount = value || null; break;
                    default: break;
                  }
                }
              });

              return mappedRow;
            }).filter(s =>
              s.transactionDate &&
              (s.debitAmount > 0 || s.creditAmount > 0)
            );
          }
        } else {
          // No template — match headers case-insensitively (works for both CSV-style Excel and regular Excel)
          const h = (name) => excelFields.find(f => f.trim().toLowerCase() === name.toLowerCase());

          const transactionDateField = h('transaction_date');
          const referenceNoField = h('reference_no') || h('reference_number');
          const descriptionField = h('description');
          const debitField = h('debit_amount') || h('debit');
          const creditField = h('credit_amount') || h('credit');

          if (!transactionDateField) {
            setUploadError(
              `Required header not found. Please ensure your file has TRANSACTION_DATE header. Found headers: ${excelFields.join(', ')}`
            );
            setUploading(false);
            return;
          }

          statementsData = excelData.map(row => {
            const transactionDate = convertValue(row[transactionDateField], 'DATE');
            const debitVal = parseFloat(row[debitField]) || 0;
            const creditVal = parseFloat(row[creditField]) || 0;

            if (!transactionDate || (debitVal <= 0 && creditVal <= 0)) return null;

            return {
              bankAccountId: selectedBankAccountApi,
              tenantId,
              transactionDate,
              referenceNo: row[referenceNoField] || '',
              description: row[descriptionField] || '',
              propertyId: selectedPropertyApi,
              debitAmount: debitVal > 0 ? debitVal : null,
              creditAmount: creditVal > 0 ? creditVal : null,
              sourceSystem: 'CSV'
            };
          }).filter(Boolean);
        }

        if (statementsData.length === 0) {
          setUploadError('No valid bank statements found in file.');
          setUploading(false);
          return;
        }

        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(statementsData),
        });

        if (!resp.ok) {
          const errorText = await resp.text();
          throw new Error(`Failed to upload bank statements: ${resp.status} ${resp.statusText} - ${errorText}`);
        }

        const result = await resp.json();
        await fetchBankStatements();

        const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
        const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
        showSuccess(`Successfully uploaded ${result.length || statementsData.length} Bank Statements for ${propertyName}`);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setSelectedPropertyApi('');
        setSelectedBankAccountApi('');
        setSelectedTemplate('');
        setCustomMappings({});
        setUploadProgress(0);
        setUploading(false);
        return;
      }

      // ── CSV TEXT PATH (plain .csv files without template) ─────────────────
      const reader = new FileReader();

      reader.onload = async (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length === 0) {
          setUploadError('File is empty');
          setUploading(false);
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

        const transactionDateIndex = headers.findIndex(h => h.trim().toLowerCase() === 'transaction_date');
        const referenceNoIndex = headers.findIndex(h => h.trim().toLowerCase() === 'reference_no' || h.trim().toLowerCase() === 'reference_number');
        const descriptionIndex = headers.findIndex(h => h.trim().toLowerCase() === 'description');
        const debitIndex = headers.findIndex(h => h.trim().toLowerCase() === 'debit_amount' || h.trim().toLowerCase() === 'debit');
        const creditIndex = headers.findIndex(h => h.trim().toLowerCase() === 'credit_amount' || h.trim().toLowerCase() === 'credit');

        if (transactionDateIndex === -1) {
          setUploadError(`Required header not found. Please ensure your file has TRANSACTION_DATE header. Found headers: ${headers.join(', ')}`);
          setUploading(false);
          return;
        }

        const statementsData = lines.slice(1).map((line) => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const transactionDate = values[transactionDateIndex];
          const debitVal = parseFloat(values[debitIndex]) || 0;
          const creditVal = parseFloat(values[creditIndex]) || 0;

          if (!transactionDate || (debitVal <= 0 && creditVal <= 0)) return null;

          let formattedDate = transactionDate;
          try {
            if (transactionDate.includes('/')) {
              const parts = transactionDate.split('/');
              if (parts.length === 3) {
                formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
              }
            }
          } catch (dateError) {
            console.warn('Date format conversion failed:', transactionDate, dateError);
          }

          return {
            bankAccountId: selectedBankAccountApi,
            tenantId,
            transactionDate: formattedDate,
            referenceNo: values[referenceNoIndex] || '',
            description: values[descriptionIndex] || '',
            propertyId: selectedPropertyApi,
            debitAmount: debitVal > 0 ? debitVal : null,
            creditAmount: creditVal > 0 ? creditVal : null,
            sourceSystem: 'CSV'
          };
        }).filter(Boolean);

        if (statementsData.length === 0) {
          setUploadError('No valid bank statements found in file.');
          setUploading(false);
          return;
        }

        try {
          const resp = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(statementsData),
          });

          if (!resp.ok) {
            const errorText = await resp.text();
            throw new Error(`Failed to upload bank statements: ${resp.status} ${resp.statusText} - ${errorText}`);
          }

          const result = await resp.json();
          await fetchBankStatements();

          const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
          const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
          showSuccess(`Successfully uploaded ${result.length || statementsData.length} Bank Statements for ${propertyName}`);
          setUploadDialogOpen(false);
          setUploadFile(null);
          setSelectedPropertyApi('');
          setSelectedBankAccountApi('');
          setUploadProgress(0);
        } catch (apiError) {
          console.error('API Error:', apiError);
          setUploadError(apiError.message || 'Failed to upload bank statements');
        } finally {
          setUploading(false);
          setUploadProgress(0);
        }
      };

      reader.onerror = () => {
        setUploadError('Failed to read the file');
        setUploading(false);
        setUploadProgress(0);
      };

      reader.readAsText(uploadFile);

    } catch (err) {
      console.error('Error uploading CSV:', err);
      setUploadError(err.message || 'Failed to upload CSV');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Helper function to get property name by ID
  const getPropertyNameById = (propertyId) => {
    if (!propertyId) return 'N/A';
    const property = propertyApis.find(p => p.propertyId === propertyId);
    return property ? property.propertyName : propertyId;
  };

  // Columns for Bank Statements Grid
  const bankStatementColumns = [
    {
      field: 'transactionDate',
      headerName: 'Transaction Date',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'referenceNo',
      headerName: 'Reference No',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 180,
    },
    {
      field: 'debitAmount',
      headerName: 'Debit',
      flex: 1,
      minWidth: 100,
      type: 'number',
      valueFormatter: (params) => params.value ? `$${params.value.toFixed(2)}` : '-',
    },
    {
      field: 'creditAmount',
      headerName: 'Credit',
      flex: 1,
      minWidth: 100,
      type: 'number',
      valueFormatter: (params) => params.value ? `$${params.value.toFixed(2)}` : '-',
    },
    // {
    //   field: 'propertyId',
    //   headerName: 'Property ID',
    //   flex: 1,
    //   minWidth: 120,
    // },
    {
      field: 'propertyId',
      headerName: 'Property Name',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => getPropertyNameById(params.value),
    },
    {
      field: 'propertyGroup',
      headerName: 'Property Group',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'sourceSystem',
      headerName: 'Source / Integration',
      flex: 1,
      minWidth: 140,
    },
    // {
    //   field: 'reconStatus',
    //   headerName: 'Reconciliation Status',
    //   flex: 1,
    //   minWidth: 140,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value}
    //       color={
    //         params.value === 'AUTO_MATCHED' ? 'success' :
    //           params.value === 'MANUAL_MATCHED' ? 'info' :
    //             params.value === 'ADJUSTED' ? 'warning' :
    //               params.value === 'APPROVED' ? 'primary' :
    //                 params.value === 'IGNORED' ? 'default' :
    //                   'error'
    //       }
    //       size="small"
    //     />
    //   ),
    // },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
          bgcolor: ht.drawerBg,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Typography variant="h6" sx={{ flex: 1, color: ht.white, fontWeight: 700 }}>
          Bank Statement Management
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={harmonateDrawerBodySx}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        {/* Bank Statements Accordion */}
        <Accordion
          expanded={expanded === 'bankStatements'}
          onChange={() => setExpanded(expanded === 'bankStatements' ? false : 'bankStatements')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Bank Statements</Typography>
            <IconButton
              onClick={handleOpenEditBankStatementDialog}
              size="small"
              disabled={!selectedBankStatementRow || selectedBankStatementRow?.isReadOnly || !canEdit}
              sx={harmonateDrawerEditIconSx}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenDeleteBankStatementDialog}
              size="small"
              disabled={!selectedBankStatementRow || !canDelete}
              sx={harmonateDrawerDeleteIconSx}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenAddBankStatementDialog}
              size="small"
              disabled={!canAdd}
              sx={harmonateDrawerAddIconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            
            {/* <IconButton
              onClick={handleOpenUploadDialog}
              size="small"
              color="primary"
            >
              <UploadIcon fontSize="small" />
            </IconButton> */}
          </AccordionSummary>
          <AccordionDetails sx={{
            ...harmonateDrawerAccordionDetailsSx,
            height: 500,
            overflow: 'hidden',
            p: 2,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Filters */}
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                {/* Row 1: Date Range */}
                <Grid item xs={6}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={dateRangeFilter.start}
                    onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={dateRangeFilter.end}
                    onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Property Group</InputLabel>
                    <Select
                      value={propertyGroupFilter}
                      label="Property Group"
                      onChange={(e) => setPropertyGroupFilter(e.target.value)}
                      disabled={bankStatementsLoading || propertyGroupsLoading}
                    >
                      <MenuItem value="ALL">All</MenuItem>
                      {propertyGroups.map(group => (
                        <MenuItem key={group.propertyGroupId} value={group.propertyGroupId}>
                          {group.groupName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid> */}
                {/* Row 2: Property and Bank */}
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disabled={bankStatementsLoading}
                    options={[{ propertyId: 'ALL', propertyName: 'All Properties' }, ...properties]}
                    getOptionLabel={(option) => {
                      if (option.propertyId === 'ALL') {
                        return option.propertyName;
                      }
                      const code = option.propertyCode || '';
                      const name = option.propertyName || '';
                      return code && name ? `${code} - ${name}` : name;
                    }}
                    value={properties.find(p => p.propertyId === propertyIdFilter) || { propertyId: 'ALL', propertyName: 'All Properties' }}
                    onChange={(event, newValue) => {
                      setPropertyIdFilter(newValue ? newValue.propertyId : 'ALL');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Property"
                        placeholder="Select Property"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {bankStatementsLoading ? (
                                <CircularProgress size={20} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          )
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                    sx={{ fontSize: '14px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Bank</InputLabel>
                    <Select
                      value={bankAccountFilter}
                      label="Bank"
                      onChange={(e) => setBankAccountFilter(e.target.value)}
                      disabled={bankStatementsLoading}
                    >
                      <MenuItem value="ALL">All</MenuItem>
                      {bankAccounts.map(account => (
                        <MenuItem key={account.bankAccountId} value={account.bankAccountId}>
                          {account.accountName || account.accountNumber}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reconciliation Status</InputLabel>
                    <Select
                      value={reconciliationStatusFilter}
                      label="Reconciliation Status"
                      onChange={(e) => setReconciliationStatusFilter(e.target.value)}
                      disabled={bankStatementsLoading}
                    >
                      <MenuItem value="ALL">All</MenuItem>
                      <MenuItem value="UNMATCHED">Unmatched</MenuItem>
                      <MenuItem value="AUTO_MATCHED">Auto Matched</MenuItem>
                      <MenuItem value="MANUAL_MATCHED">Manual Matched</MenuItem>
                      <MenuItem value="ADJUSTED">Adjusted</MenuItem>
                      <MenuItem value="APPROVED">Approved</MenuItem>
                      <MenuItem value="IGNORED">Ignored</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}
              </Grid>
            </Box>

            {bankStatementsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress />
              </Box>
            ) : bankStatementsError ? (
              <Alert severity="error">{bankStatementsError}</Alert>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 350 }}>
                <DataGridPro
                  rows={bankStatements}
                  columns={bankStatementColumns}
                  columnResizeMode="onEnd"
                  resizable
                  sx={{ border: 'none', flex: 1 }}
                  getRowId={(row) => row.id}
                  rowHeight={35}
                  onRowSelectionModelChange={(newSelection) => {
                    if (newSelection.length > 0) {
                      const selectedRow = bankStatements.find(row => row.id === newSelection[0]);
                      setSelectedBankStatementRow(selectedRow);
                    } else {
                      setSelectedBankStatementRow(null);
                    }
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography>No bank statements found.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSize={100}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Upload Bank Statements
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <DialogContentText sx={{ mb: 2, fontSize: '14px' }}>
            Select a Property, Bank and upload your Bank Statement data file.
          </DialogContentText>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeUploadTab}
              onChange={(e, newValue) => setActiveUploadTab(newValue)}
              aria-label="Upload Bank Statements tabs"
            >
              <Tab label="Excel" {...a11yProps(0)} />
              {/* <Tab label="Refresh Data" {...a11yProps(1)} /> */}
            </Tabs>
          </Box>

          {/* FROM EXCEL TAB */}
          <CustomTabPanel value={activeUploadTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Property Dropdown */}
                {/* <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Property <span style={{ color: 'red' }}>*</span>
                </Typography> */}
                {loadingPropertyApis ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>Loading Properties...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    fullWidth
                    options={propertyApis}
                    getOptionLabel={(option) => {
                      const code = option.propertyCode || '';
                      const name = option.propertyName || '';
                      return code && name ? `${code} - ${name}` : name;
                    }}
                    value={propertyApis.find(p => p.propertyId === selectedPropertyApi) || null}
                    onChange={(event, newValue) => {
                      setSelectedPropertyApi(newValue ? newValue.propertyId : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select a Property"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingPropertyApis ? (
                                <CircularProgress size={20} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          )
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                    sx={{ fontSize: '14px' }}
                  />
                )}

                {/* Bank Account Dropdown */}
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Bank <span style={{ color: 'red' }}>*</span></InputLabel>
                  {bankAccountsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>Loading Bank...</Typography>
                    </Box>
                  ) : (
                    <Select
                      value={selectedBankAccountApi}
                      label="Bank Account"
                      onChange={(e) => setSelectedBankAccountApi(e.target.value)}
                      disabled={bankAccountsLoading}
                    >
                      <MenuItem value="">Select a Bank</MenuItem>
                      {bankAccounts.map((account) => (
                        <MenuItem key={account.bankAccountId} value={account.bankAccountId}>
                          {account.accountName || account.accountNumber}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Required Headers Information */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: ht.listBg, borderRadius: '4px', border: `1px solid ${ht.line}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
                Required Headers in your file:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { fontSize: '13px', color: '#495057', lineHeight: 1.7 } }}>
                <li><strong>TRANSACTION_DATE</strong> - The transaction date (mandatory)</li>
                <li><strong>REFERENCE_NUMBER</strong> - The reference number (mandatory)</li>
                <li><strong>DESCRIPTION</strong> - The description (mandatory)</li>
                <li><strong>DEBIT/CREDIT</strong> - The debit/credit amount (mandatory)</li>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6c757d', fontSize: '12px' }}>
                Note: The file must be in CSV or Excel format with these exact header names in the first row.
              </Typography>
            </Box>

            {/* Upload Area */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Upload File
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000', mb: 2 }}>
                Add your Bank Statements file here
              </Typography>

              <HiddenInput
                id="csv-upload-input"
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                      setUploadFile(file);
                      setUploadError(null);
                    } else {
                      setUploadError('Please select a CSV or Excel file');
                    }
                  }
                }}
              />
              <label htmlFor="csv-upload-input">
                <UploadArea
                  onClick={() => document.getElementById('csv-upload-input').click()}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    borderColor: isDragging ? ht.green : undefined,
                    backgroundColor: isDragging ? ht.secondaryTab : undefined,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 32, color: ht.green, mb: 1 }} />
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}>
                    {isDragging ? 'Drop your file here' : 'Drop your file or browse'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#000000', fontSize: '11px' }}>
                    Max 10 MB files are allowed
                  </Typography>
                </UploadArea>
              </label>

              {uploadFile && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 2,
                    mb: 2,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: '#e8f5e9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <InsertDriveFileIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        color: '#000000',
                        fontSize: '14px',
                        mb: 0.5
                      }}>
                        {uploadFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#000000', fontSize: '12px' }}>
                        {(uploadFile.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setUploadFile(null);
                      setUploadError(null);
                      setUploadProgress(0);
                    }}
                    sx={{
                      color: '#000000',
                      '&:hover': { color: '#d32f2f', backgroundColor: '#ffebee' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Paper>
              )}
              <Typography variant="caption" sx={{ color: '#000000', fontSize: '10px', display: 'block', mt: 1.5, mb: 2 }}>
                Only support CSV and XLS/XLSX
              </Typography>
            </Box>

            {/* Template Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Template</InputLabel>
              {loadingTemplates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>Loading Templates...</Typography>
                </Box>
              ) : (
                <Select
                  value={selectedTemplate}
                  label="Template"
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={loadingTemplates}
                >
                  <MenuItem value="">No template selected</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>

            {/* Preview Button */}
            {selectedTemplate && uploadFile && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePreviewClick}
                sx={harmonateUploadOutlinedSx}
                >
                  Preview & Map Columns
                </Button>
              </Box>
            )}

            {/* Error Message */}
            {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}

            {/* Progress Bar */}
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </CustomTabPanel>

          {/* FROM SNOWFLAKE TAB */}
          <CustomTabPanel value={activeUploadTab} index={1}>
            {/* Row 1: Property and Dates */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                {loadingPropertyApis ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>Loading Properties...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    fullWidth
                    options={propertyApis}
                    getOptionLabel={(option) => {
                      const code = option.propertyCode || '';
                      const name = option.propertyName || '';
                      return code && name ? `${code} - ${name}` : name;
                    }}
                    value={propertyApis.find(p => p.propertyId === selectedPropertyApi) || null}
                    onChange={(event, newValue) => {
                      setSelectedPropertyApi(newValue ? newValue.propertyId : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Property"
                        size="small"
                        placeholder="Select Property"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingPropertyApis ? (
                                <CircularProgress size={20} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          )
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                    sx={{ fontSize: '14px' }}
                  />
                )}
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="From Date"
                  type="date"
                  value={snowflakeFromDate}
                  onChange={(e) => setSnowflakeFromDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="To Date"
                  type="date"
                  value={snowflakeToDate}
                  onChange={(e) => setSnowflakeToDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Row 2: Bank Account */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Bank</InputLabel>
                  {bankAccountsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>Loading Bank...</Typography>
                    </Box>
                  ) : (
                    <Select
                      value={selectedBankAccountApi}
                      label="Bank Account"
                      onChange={(e) => setSelectedBankAccountApi(e.target.value)}
                      disabled={bankAccountsLoading}
                    >
                      <MenuItem value="">Select Bank</MenuItem>
                      {bankAccounts.map((account) => (
                        <MenuItem key={account.bankAccountId} value={account.bankAccountId}>
                          {account.accountName || account.accountNumber}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {/* Load Button */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={fetchSnowflakeData}
                disabled={!selectedPropertyApi || snowflakeLoading}
                sx={harmonateProceedButtonSx}
              >
                {snowflakeLoading && <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />}
                Load Data from Snowflake
              </Button>
            </Box>

            {/* Error Message */}
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}

            {/* Snowflake Data Grid */}
            <Box sx={{ height: 350 }}>
              {snowflakeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>Loading Snowflake data...</Typography>
                </Box>
              ) : snowflakeError ? (
                <Alert severity="error" onClose={() => setSnowflakeError(null)}>{snowflakeError}</Alert>
              ) : (
                <DataGridPro
                  rows={snowflakeData}
                  columns={[
                    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
                    { field: 'date', headerName: 'Date', width: 120 },
                    { field: 'docNo', headerName: 'Doc No', width: 150 },
                    {
                      field: 'debit', headerName: 'Debit', width: 120, type: 'number',
                      valueFormatter: (params) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value || 0),
                    },
                    {
                      field: 'credit', headerName: 'Credit', width: 120, type: 'number',
                      valueFormatter: (params) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value || 0),
                    },
                  ]}
                  rowHeight={40}
                  getRowId={(row) => row.id}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', fontWeight: 600 },
                    '& .MuiDataGrid-columnSeparator': { display: 'none' },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography>No Snowflake data found. Select a property and load data.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSizeOptions={[10, 25, 50]}
                  pageSize={25}
                />
              )}
            </Box>
          </CustomTabPanel>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedTemplate('');
              setCustomMappings({});
              setUploadProgress(0);
              setUploadError(null);
              setIsDragging(false);
              setActiveUploadTab(0); // Reset to first tab
              setSnowflakeData([]);
              setSnowflakeError(null);
              setSnowflakeFromDate(''); // Reset Snowflake date fields
              setSnowflakeToDate(''); // Reset Snowflake date fields
              setDateRangeFilter({ start: '', end: '' }); // Reset date range filters
            }}
            disabled={uploading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          {activeUploadTab === 0 && (
            <Button
              onClick={handleCsvUpload}
              variant="contained"
              disabled={!selectedPropertyApi || !selectedBankAccountApi || !uploadFile || uploading}
              sx={harmonateProceedButtonSx}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
          {activeUploadTab === 1 && (
            <Button
              onClick={handleSaveSnowflakeData}
              variant="contained"
              disabled={!selectedPropertyApi || !selectedBankAccountApi || snowflakeData.length === 0 || uploading}
              sx={harmonateProceedButtonSx}
            >
              {uploading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Preview Dialog for Column Mapping */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Preview & Map Columns - {previewData.templateName}
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              File: <strong>{previewData.fileName}</strong>
            </Typography>

            {/* Column Mapping Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Excel Column</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Template Field</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Data Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Required</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.excelFields.map((excelField) => {
                    const mappedTemplateField = Object.keys(customMappings).find(key => key === excelField && customMappings[key]) ||
                      Object.keys(previewData.mappings).find(key => previewData.mappings[key]?.excelField === excelField) || '';
                    const template = templates.find(t => t.id === selectedTemplate);
                    const mapping = template?.mappings?.[customMappings[excelField] || mappedTemplateField];

                    return (
                      <TableRow key={excelField}>
                        <TableCell>{excelField}</TableCell>
                        <TableCell>
                          <Select
                            value={customMappings[excelField] || mappedTemplateField}
                            onChange={(e) => handleCustomMapping(excelField, e.target.value)}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={{ fontSize: '13px' }}
                          >
                            <MenuItem value="">
                              <em>Do not map</em>
                            </MenuItem>
                            {getAvailableTemplateFields(excelField, customMappings[excelField] || mappedTemplateField).map((field) => {
                              const fieldMapping = template?.mappings?.[field];
                              return (
                                <MenuItem key={field} value={field} sx={{ fontSize: '13px' }}>
                                  {field} {fieldMapping?.isRequired && ' *'}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {mapping ? (
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {mapping.dataType || 'TEXT'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping?.isRequired ? (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#d32f2f', fontWeight: 600 }}>
                              Yes
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000' }}>
                              No
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Preview Data */}
          {previewData.excelData.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Preview Data (First 5 rows)
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {previewData.templateFields.map((field) => (
                        <TableCell key={field} sx={{ fontWeight: 600, fontSize: '12px' }}>
                          {field} {templates.find(t => t.id === selectedTemplate)?.mappings?.[field]?.isRequired && '*'}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.excelData.slice(0, 5).map((row, index) => (
                      <TableRow key={index}>
                        {previewData.templateFields.map((field) => (
                          <TableCell key={field} sx={{ fontSize: '12px' }}>
                            {row[field] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setPreviewOpen(false)}
            sx={{ fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePreviewApply}
            variant="contained"
            sx={harmonateProceedButtonSx}
          >
            Apply Mappings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Bank Statement Dialog */}
      <Dialog
        open={addBankStatementDialogOpen}
        onClose={() => setAddBankStatementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Add Bank Statement
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {bankAccountsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, width: '100%' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading Bank...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  sx={{ flex: 1, minWidth: 200, fontSize: '14px' }}
                  options={bankAccounts}
                  getOptionLabel={(option) =>
                    option?.accountName || option?.accountNumber || ''
                  }
                  value={
                    bankAccounts.find(
                      (a) => String(a.bankAccountId) === String(newBankStatement.bankAccountId)
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setNewBankStatement({
                      ...newBankStatement,
                      bankAccountId: newValue ? newValue.bankAccountId : '',
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bank"
                      required
                      size="small"
                      placeholder="Select Bank"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {bankAccountsLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.bankAccountId) === String(value?.bankAccountId)
                  }
                />
              )}
              {loadingPropertyApis ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, width: '100%' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading Properties...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  sx={{ flex: 1, minWidth: 200, fontSize: '14px' }}
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find(p => p.propertyId === newBankStatement.propertyId) || null}
                  onChange={(event, newValue) => {
                    setNewBankStatement({ ...newBankStatement, propertyId: newValue ? newValue.propertyId : '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Property"
                      required
                      size="small"
                      placeholder="Select Property"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPropertyApis ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Transaction Date"
                type="date"
                size="small"
                value={newBankStatement.transactionDate}
                onChange={(e) => setNewBankStatement({ ...newBankStatement, transactionDate: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                label="Reference No"
                size="small"
                value={newBankStatement.referenceNo}
                onChange={(e) => setNewBankStatement({ ...newBankStatement, referenceNo: e.target.value })}
                fullWidth
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>

            <TextField
              label="Description"
              size="small"
              value={newBankStatement.description}
              onChange={(e) => setNewBankStatement({ ...newBankStatement, description: e.target.value })}
              fullWidth
              required
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <FormControl fullWidth size="small" sx={{ flex: '0 1 200px', minWidth: 160 }}>
                <InputLabel id="bs-add-amount-type-label">Type</InputLabel>
                <Select
                  labelId="bs-add-amount-type-label"
                  label="Type"
                  value={newBankStatement.amountType}
                  onChange={(e) => setNewBankStatement({
                    ...newBankStatement,
                    amountType: e.target.value,
                    amount: null
                  })}
                  required
                >
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={newBankStatement.amountType === 'debit' ? 'Debit Amount' : 'Credit Amount'}
                type="number"
                size="small"
                value={newBankStatement.amount || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseFloat(e.target.value) : null;
                  setNewBankStatement({
                    ...newBankStatement,
                    amount: value
                  });
                }}
                fullWidth
                required
                sx={{ flex: 1, minWidth: 160 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                }}
                inputProps={{
                  step: '0.01',
                  min: 0,
                }}
              />
            </Box>

            {/* Error Message */}
            {addBankStatementError && (
              <Alert severity="error">
                {addBankStatementError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setAddBankStatementDialogOpen(false)}
            disabled={addBankStatementLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddBankStatement}
            variant="contained"
            disabled={addBankStatementLoading}
            sx={harmonateProceedButtonSx}
          >
            {addBankStatementLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bank Statement Dialog */}
      <Dialog
        open={editBankStatementDialogOpen}
        onClose={() => setEditBankStatementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Edit Bank Statement
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {bankAccountsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, width: '100%' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading Bank...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  sx={{ flex: 1, minWidth: 200, fontSize: '14px' }}
                  options={bankAccounts}
                  getOptionLabel={(option) =>
                    option?.accountName || option?.accountNumber || ''
                  }
                  value={
                    bankAccounts.find(
                      (a) =>
                        String(a.bankAccountId) ===
                        String(editingBankStatement?.bankAccountId ?? '')
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setEditingBankStatement({
                      ...editingBankStatement,
                      bankAccountId: newValue ? newValue.bankAccountId : '',
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bank"
                      required
                      size="small"
                      placeholder="Select Bank"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {bankAccountsLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.bankAccountId) === String(value?.bankAccountId)
                  }
                />
              )}
              {loadingPropertyApis ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, width: '100%' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading Properties...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  sx={{ flex: 1, minWidth: 200, fontSize: '14px' }}
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find(p => p.propertyId === editingBankStatement?.propertyId) || null}
                  onChange={(event, newValue) => {
                    setEditingBankStatement({ ...editingBankStatement, propertyId: newValue ? newValue.propertyId : '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Property"
                      required
                      size="small"
                      placeholder="Select Property"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPropertyApis ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Transaction Date"
                type="date"
                size="small"
                value={editingBankStatement?.transactionDate || ''}
                onChange={(e) => setEditingBankStatement({ ...editingBankStatement, transactionDate: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                label="Reference No"
                size="small"
                value={editingBankStatement?.referenceNo || ''}
                onChange={(e) => setEditingBankStatement({ ...editingBankStatement, referenceNo: e.target.value })}
                fullWidth
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>

            <TextField
              label="Description"
              size="small"
              value={editingBankStatement?.description || ''}
              onChange={(e) => setEditingBankStatement({ ...editingBankStatement, description: e.target.value })}
              fullWidth
              required
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <FormControl fullWidth size="small" sx={{ flex: '0 1 200px', minWidth: 160 }}>
                <InputLabel id="bs-edit-amount-type-label">Type</InputLabel>
                <Select
                  labelId="bs-edit-amount-type-label"
                  label="Type"
                  value={editingBankStatement?.amountType || 'debit'}
                  onChange={(e) => setEditingBankStatement({
                    ...editingBankStatement,
                    amountType: e.target.value,
                    amount: null
                  })}
                  required
                >
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={editingBankStatement?.amountType === 'debit' ? 'Debit Amount' : 'Credit Amount'}
                type="number"
                size="small"
                value={editingBankStatement?.amount || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseFloat(e.target.value) : null;
                  setEditingBankStatement({
                    ...editingBankStatement,
                    amount: value
                  });
                }}
                fullWidth
                required
                sx={{ flex: 1, minWidth: 160 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                }}
                inputProps={{
                  step: '0.01',
                  min: 0,
                }}
              />
            </Box>

            {/* Error Message */}
            {editBankStatementError && (
              <Alert severity="error">
                {editBankStatementError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setEditBankStatementDialogOpen(false)}
            disabled={editBankStatementLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditBankStatement}
            variant="contained"
            disabled={editBankStatementLoading}
            sx={harmonateProceedButtonSx}
          >
            {editBankStatementLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Bank Statement Dialog */}
      <Dialog open={deleteBankStatementDialogOpen} onClose={() => setDeleteBankStatementDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {deleteBankStatementError ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {deleteBankStatementError}
            </Alert>
          ) : (
            <Typography>
              Are you sure you want to delete bank statement "{deletingBankStatement?.description || deletingBankStatement?.referenceNo}"?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteBankStatementDialogOpen(false)} disabled={deleteBankStatementLoading}>
            Cancel
          </Button>
          {!deleteBankStatementError && (
            <Button
              onClick={handleDeleteBankStatement}
              variant="contained"
              disabled={deleteBankStatementLoading}
            sx={harmonateAbortButtonSx}
            >
              {deleteBankStatementLoading ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default BankStatementDrawer;
